import { useState } from 'react';
import {
  Button,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRedirect
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  CircularProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { supabase } from '../lib/supabaseClient';
import { syncAddOnFlagsForFuneral } from '../dataProviders/syncAddOnFlagsForFuneral';

const atNeedLines = [
  { item: '1010000041' },
];

/**
 * Insert into funeral_clients, ignoring duplicates (unique constraint).
 */
async function linkClientToFuneral(funeralId: number, clientId: number, relationship?: string) {
  await supabase
    .from('funeral_clients')
    .upsert(
      { funeral_id: funeralId, client_id: clientId, relationship: relationship || null },
      { onConflict: 'client_id,funeral_id' },
    );
}

export const QualifyButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();

  if (!record) return null;

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      // 1. Create deceased client
      const { data: newClient } = await dataProvider.create('clients', {
        data: {
          name: record.deceased_first_name + ' ' + record.deceased_last_name,
          first_name: record.deceased_first_name,
          last_name: record.deceased_last_name,
          email: record.email,
          phone_number: record.phone_number,
        },
      });

      // 2. Create funeral
      const { data: newFuneral } = await dataProvider.create('funerals', {
        data: {
          deceased_client_id: newClient.id,
          primary_client_id: record.client_id,
          ...(record.region && { region: record.region }),
        },
      });

      // 3. Update quote as qualified
      await dataProvider.update('quotes', {
        id: record.id,
        data: {
          qualified: true,
          funeral_id: newFuneral.id,
        },
        previousData: record,
      });

      // 4. Link the quote's people to the funeral
      try {
        const linkedClientIds = new Set<number>();

        // 4a. The quote's own person
        if (record.client_id) {
          await linkClientToFuneral(newFuneral.id, record.client_id);
          linkedClientIds.add(Number(record.client_id));
        }

        // 4b. Link all quote_contacts' clients
        const { data: quoteContacts } = await dataProvider.getList('quote_contacts', {
          filter: { quote_id: record.id },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'created_at', order: 'ASC' },
        });

        if (quoteContacts && quoteContacts.length > 0) {
          for (const qc of quoteContacts) {
            const clientId = Number(qc.client_id);
            if (!clientId || linkedClientIds.has(clientId)) continue;
            await linkClientToFuneral(newFuneral.id, clientId, qc.role || undefined);
            linkedClientIds.add(clientId);
          }
        }
      } catch (err) {
        console.error('Error linking quote contacts to funeral:', err);
        // Non-fatal — funeral was already created
      }

      // 5. Create contract
      await dataProvider.create('contracts', {
        data: {
          funeral_id: newFuneral.id,
          client_id: record.client_id,
          lines: atNeedLines,
          is_preneed: false,
        },
      });
      // Written straight to PostgREST, so keep the funeral's add-on flags in
      // step with the lines the way the NestJS contract writes do.
      await syncAddOnFlagsForFuneral(newFuneral.id);

      notify('Quote qualified, funeral and contract created successfully', { type: 'success' });
      redirect('show', 'funerals', newFuneral.id);
    } catch (error) {
      console.error('Error qualifying quote:', error);
      notify('Error qualifying quote', { type: 'error' });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  if (record.qualified) {
    return null;
  }

  return (
    <>
      <Button
        label="Qualify"
        onClick={handleClick}
        disabled={loading}
      >
        <CheckCircleOutlineIcon />
      </Button>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Qualify Quote</DialogTitle>
        <DialogContent>
          Qualify this quote and create a new funeral with contract?
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleClose} disabled={loading}>
            Cancel
          </MuiButton>
          <MuiButton 
            onClick={handleConfirm} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Create Funeral & Contract'
            )}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
}; 